import { useCallback, useEffect, useState } from "react";
import { Alert, Button, Drawer, Input, Spin, Table } from "antd";
import { LeftOutlined, RightOutlined, SearchOutlined } from "@ant-design/icons";
import type { TableColumnsType } from "antd";
import { AdminListShell } from "../components/admin/AdminListShell";
import { fetchAdminUserDetail, fetchAdminUsersPage } from "../api/client";
import type { AdminUserDetailResponse, AdminUserListItem } from "../api/types";
import { fmtDateTime } from "../lib/formatAdmin";
import { roleLabelZh, userStatusLedgerTag, userStatusLedgerText } from "../lib/userDisplay";

function fmtPts(n: number | null | undefined) {
  const v = typeof n === "number" && !Number.isNaN(n) ? n : 0;
  return `${v.toLocaleString("zh-CN")} 点`;
}

function RegionCell({
  region,
  ip,
}: {
  region: string | null | undefined;
  ip: string | null | undefined;
}) {
  const r = region?.trim() || "—";
  const i = ip?.trim() || "—";
  return (
    <div>
      <div style={{ fontSize: 12 }}>{r}</div>
      <div className="admin-ledger-mono">{i}</div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const t = userStatusLedgerTag(status);
  const cls =
    t === "success"
      ? "admin-tag-ledger admin-tag-ledger--success"
      : t === "error"
        ? "admin-tag-ledger admin-tag-ledger--error"
        : "admin-tag-ledger admin-tag-ledger--pending";
  return <span className={cls}>{userStatusLedgerText(status)}</span>;
}

export function UsersPage() {
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState<AdminUserListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<"forbidden" | "error" | null>(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<AdminUserDetailResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailErr, setDetailErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    const r = await fetchAdminUsersPage({ page, page_size: pageSize, q: q || undefined });
    if (r === "forbidden") setErr("forbidden");
    else if (r === "error") setErr("error");
    else {
      setItems(r.items);
      setTotal(r.total);
    }
    setLoading(false);
  }, [page, pageSize, q]);

  useEffect(() => {
    const t = window.setTimeout(() => setQ(qInput.trim()), 380);
    return () => window.clearTimeout(t);
  }, [qInput]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [q]);

  const openDetail = (id: string) => {
    setDetailId(id);
    setDrawerOpen(true);
    setDetail(null);
    setDetailErr(null);
    setDetailLoading(true);
    void (async () => {
      const r = await fetchAdminUserDetail(id);
      if (r === "forbidden") setDetailErr("无权限查看该用户");
      else if (r === "error") setDetailErr("无法加载详情，请检查网络或后端");
      else setDetail(r);
      setDetailLoading(false);
    })();
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setDetailId(null);
    setDetail(null);
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  const columns: TableColumnsType<AdminUserListItem> = [
    {
      title: "用户信息",
      key: "user",
      width: 220,
      render: (_, row) => (
        <div>
          <div className="admin-ledger-email">{row.email}</div>
          <div className="admin-ledger-name-sub">{row.name?.trim() || "—"}</div>
        </div>
      ),
    },
    {
      title: "角色",
      dataIndex: "role",
      key: "role",
      width: 110,
      render: (r: string) => roleLabelZh(r),
    },
    {
      title: "状态",
      dataIndex: "status",
      key: "status",
      width: 100,
      align: "center",
      render: (s: string) => <StatusBadge status={s} />,
    },
    {
      title: "额度余额",
      dataIndex: "credit_balance",
      key: "bal",
      width: 120,
      align: "right",
      render: (n: number | undefined) => (
        <span className="admin-ledger-num">{fmtPts(n)}</span>
      ),
    },
    {
      title: "累计使用",
      dataIndex: "credit_total_used",
      key: "used",
      width: 120,
      align: "right",
      render: (n: number | undefined) => (
        <span className="admin-ledger-num admin-ledger-num--sub">{fmtPts(n)}</span>
      ),
    },
    {
      title: "最近订阅套餐",
      key: "sub",
      width: 180,
      render: (_, row) => {
        const s = row.subscription_latest;
        if (!s) return <span style={{ color: "#717c82", fontStyle: "italic" }}>无活动订阅</span>;
        return (
          <div>
            <div style={{ fontSize: 12, fontWeight: 600 }}>{s.plan_name}</div>
            <div className="admin-ledger-mono" style={{ opacity: 0.75 }}>
              {s.plan_code}
            </div>
          </div>
        );
      },
    },
    {
      title: "注册地区 / IP",
      key: "reg",
      width: 150,
      render: (_, row) => <RegionCell region={row.signup_region} ip={row.signup_ip} />,
    },
    {
      title: "最近访问时间",
      dataIndex: "updated_at",
      key: "seen",
      width: 148,
      render: (iso: string) => (
        <span className="admin-ledger-mono" style={{ fontSize: 11 }}>
          {fmtDateTime(iso)}
        </span>
      ),
    },
    {
      title: "操作",
      key: "op",
      width: 88,
      align: "center",
      fixed: "right",
      render: (_, row) => (
        <Button type="link" size="small" onClick={() => openDetail(row.id)}>
          详情
        </Button>
      ),
    },
  ];

  return (
    <>
      <AdminListShell
        title="用户管理"
        description="全局用户资产与权限概览，支持深度查询及全量字段视图。额度为扫描点数（非人民币）。"
        err={err}
        errorBanner
        toolbar={
          <div className="admin-ledger-toolbar">
            <div className="admin-ledger-toolbar-search">
              <SearchOutlined style={{ color: "#717c82" }} />
              <Input
                placeholder="按邮箱或角色搜索"
                variant="borderless"
                value={qInput}
                onChange={(e) => setQInput(e.target.value)}
                allowClear
              />
            </div>
            <div className="admin-ledger-toolbar-divider" />
            <div className="admin-ledger-range">
              <span>
                {from}-{to} / {total.toLocaleString("zh-CN")}
              </span>
              <button
                type="button"
                className="admin-ledger-nav-arrow"
                disabled={page <= 1}
                aria-label="上一页"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <LeftOutlined />
              </button>
              <button
                type="button"
                className="admin-ledger-nav-arrow"
                disabled={page >= totalPages}
                aria-label="下一页"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                <RightOutlined />
              </button>
            </div>
          </div>
        }
        table={(bodyY) => (
          <div className="admin-ledger-table-wrap admin-ledger-table-wrap--flex">
            <Spin spinning={loading}>
              <Table<AdminUserListItem>
                rowKey="id"
                className="admin-ledger-table"
                columns={columns}
                dataSource={items}
                pagination={{
                  current: page,
                  pageSize,
                  total,
                  showSizeChanger: true,
                  pageSizeOptions: [10, 20, 50],
                  showTotal: (t) => `共 ${t} 条`,
                  onChange: (p, ps) => {
                    setPage(p);
                    setPageSize(ps);
                  },
                }}
                scroll={{ x: 1600, y: bodyY }}
                locale={{ emptyText: "暂无用户" }}
              />
            </Spin>
          </div>
        )}
      />

      <Drawer
        title={null}
        placement="right"
        size="40%"
        onClose={closeDrawer}
        open={drawerOpen}
        destroyOnHidden
        className="admin-drawer"
        closable
      >
        <div className="admin-drawer-kicker">USER DETAIL</div>
        <div className="admin-drawer-title-row">
          {detail?.user ? (
            <>
              <StatusBadge status={detail.user.status} />
              <h2 className="admin-drawer-title">{detail.user.email}</h2>
            </>
          ) : (
            <h2 className="admin-drawer-title">{detailId ?? "—"}</h2>
          )}
        </div>
        {detail?.user ? (
          <p className="admin-ledger-mono" style={{ margin: "8px 0 0", opacity: 0.75 }}>
            ID · {detail.user.id}
          </p>
        ) : null}

        {detailLoading ? (
          <div style={{ padding: "48px 0", textAlign: "center" }}>
            <Spin />
          </div>
        ) : detailErr ? (
          <Alert type="error" showIcon message={detailErr} style={{ marginTop: 24 }} />
        ) : detail ? (
          <>
            <section className="admin-drawer-section">
              <h3 className="admin-drawer-section-title">基本账户信息</h3>
              <div className="admin-drawer-kv">
                <div className="admin-drawer-kv-item">
                  <label>显示名称</label>
                  <div className="val">{detail.user.name?.trim() || "—"}</div>
                </div>
                <div className="admin-drawer-kv-item">
                  <label>角色</label>
                  <div className="val">{roleLabelZh(detail.user.role)}</div>
                </div>
                <div className="admin-drawer-kv-item">
                  <label>语言 / 主题</label>
                  <div className="val">
                    {detail.user.language} · {detail.user.theme_pref}
                  </div>
                </div>
                <div className="admin-drawer-kv-item">
                  <label>更新时间</label>
                  <div className="val">{fmtDateTime(detail.user.updated_at)}</div>
                </div>
              </div>
            </section>

            <section className="admin-drawer-section">
              <h3 className="admin-drawer-section-title">登录邮箱</h3>
              <div className="admin-drawer-card">
                <div className="val">{detail.user.email}</div>
              </div>
            </section>

            <section className="admin-drawer-section">
              <h3 className="admin-drawer-section-title">额度账户</h3>
              <div className="admin-drawer-kv">
                <div className="admin-drawer-kv-item">
                  <label>当前余额</label>
                  <div className="val val--accent">
                    {detail.credit_account != null
                      ? fmtPts(detail.credit_account.balance)
                      : "—"}
                  </div>
                </div>
                <div className="admin-drawer-kv-item">
                  <label>累计消耗</label>
                  <div className="val">
                    {detail.credit_account != null
                      ? fmtPts(detail.credit_account.total_used)
                      : "—"}
                  </div>
                </div>
                <div className="admin-drawer-kv-item">
                  <label>累计发放</label>
                  <div className="val">
                    {detail.credit_account != null
                      ? fmtPts(detail.credit_account.total_granted)
                      : "—"}
                  </div>
                </div>
                <div className="admin-drawer-kv-item">
                  <label>额度账更新</label>
                  <div className="val">{fmtDateTime(detail.credit_account?.updated_at)}</div>
                </div>
              </div>
            </section>

            <section className="admin-drawer-section">
              <h3 className="admin-drawer-section-title">最近订阅</h3>
              {detail.subscription_latest ? (
                <div className="admin-drawer-card">
                  <div className="admin-drawer-kv">
                    <div className="admin-drawer-kv-item">
                      <label>套餐代码</label>
                      <div className="val">{detail.subscription_latest.plan_code}</div>
                    </div>
                    <div className="admin-drawer-kv-item">
                      <label>套餐名称</label>
                      <div className="val val--accent">{detail.subscription_latest.plan_name}</div>
                    </div>
                    <div className="admin-drawer-kv-item">
                      <label>状态</label>
                      <div className="val">{detail.subscription_latest.status}</div>
                    </div>
                    <div className="admin-drawer-kv-item">
                      <label>周期</label>
                      <div className="val">
                        {fmtDateTime(detail.subscription_latest.starts_at)} —{" "}
                        {fmtDateTime(detail.subscription_latest.ends_at)}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <p style={{ color: "#717c82", margin: 0 }}>无订阅记录</p>
              )}
            </section>

            <section className="admin-drawer-section">
              <h3 className="admin-drawer-section-title">注册与访问</h3>
              <div className="admin-drawer-kv">
                <div className="admin-drawer-kv-item">
                  <label>注册时间</label>
                  <div className="val">{fmtDateTime(detail.user.created_at)}</div>
                </div>
                <div className="admin-drawer-kv-item">
                  <label>注册 IP / 地区</label>
                  <div className="val">
                    {detail.user.signup_ip || "—"} · {detail.user.signup_region || "—"}
                  </div>
                </div>
                <div className="admin-drawer-kv-item">
                  <label>最近访问 IP / 地区</label>
                  <div className="val">
                    {detail.user.last_seen_ip || "—"} · {detail.user.last_seen_region || "—"}
                  </div>
                </div>
              </div>
            </section>

            <div className="admin-drawer-footer">
              <button type="button" className="admin-btn-primary-ledger" onClick={closeDrawer}>
                关闭
              </button>
            </div>
          </>
        ) : (
          <Alert type="warning" showIcon message="无数据" style={{ marginTop: 24 }} />
        )}
      </Drawer>
    </>
  );
}
